<template>
    <v-form ref="form" @submit.prevent="save">
        <v-card>
            <card-header :title="title" />
            <v-card-text>
                <v-row>
                    <v-col cols="12" xs="12" sm="6" md="4">
                        <v-currency-field
                            v-model="activation.value"
                            :label="$t('resources.activation.attributes.value')"
                            :hint="$t('resources.activation.hints.value')"
                            :error-messages="valueErrors"
                            @input="$v.model.value.$touch()"
                            @blur="$v.model.value.$touch()"
                        ></v-currency-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="6" md="4">
                        <v-text-field
                            v-model="activation.installments"
                            type="number"
                            :label="
                                $t(
                                    'resources.activation.attributes.installments',
                                )
                            "
                            :hint="
                                $t('resources.activation.hints.installments')
                            "
                            :error-messages="installmentsErrors"
                            @input="$v.model.installments.$touch()"
                            @blur="$v.model.installments.$touch()"
                        />
                    </v-col>
                    <v-col cols="12" xs="12" sm="6" md="4">
                        <v-text-field
                            v-model="activation.amount"
                            type="number"
                            :label="
                                $t('resources.activation.attributes.amount')
                            "
                            :hint="$t('resources.activation.hints.amount')"
                            :error-messages="amountErrors"
                            @input="$v.model.amount.$touch()"
                            @blur="$v.model.amount.$touch()"
                        />
                    </v-col>
                    <v-col cols="12" xs="12" sm="6" md="4">
                        <v-radio-group
                            v-model="activation.activatable_type"
                            :label="
                                $t(
                                    'resources.activation.attributes.activatable_type',
                                )
                            "
                            @input="$v.model.activatable_type.$touch()"
                            @blur="$v.model.activatable_type.$touch()"
                            row
                        >
                            <v-radio
                                :label="
                                    $t('resources.activation.labels.module')
                                "
                                :value="typeModule"
                            />
                            <v-radio
                                :label="
                                    $t(
                                        'resources.activation.labels.plan_package',
                                    )
                                "
                                :value="typePlanPackage"
                            />
                        </v-radio-group>
                    </v-col>
                    <v-col cols="12" xs="12" sm="6" md="8">
                        <v-select
                            v-if="activation.activatable_type === typeModule"
                            v-model="activation.activatable_id"
                            :items="modules"
                            :label="
                                $t('resources.activation.attributes.module')
                            "
                            clearable
                            item-text="name"
                            item-value="id"
                            :loading="loadingModules"
                            @input="$v.model.amount.$touch()"
                            @blur="$v.model.amount.$touch()"
                            ref="activatable_id"
                        ></v-select>

                        <model-select
                            v-if="
                                activation.activatable_type === typePlanPackage
                            "
                            v-model="activation.activatable_id"
                            name="activatable_id"
                            :model="planPackageModel"
                            :item-text="'name'"
                            :item-value="'id'"
                        />
                    </v-col>
                </v-row>
            </v-card-text>
            <form-actions :loadingSave="loading" />
        </v-card>
    </v-form>
</template>

<script>
import Activation from '@/models/Activation'
import Module from '@/models/plans/Module'
import PlanPackage from '@/models/plans/PlanPackage'
import { validationMixin } from 'vuelidate'
import { required, requiredIf, between } from 'vuelidate/lib/validators'
import FormActions from '@/shared/components/FormActions'
import ModelSelect from '@/shared/inputs/ModelSelect'
import { isObject } from 'lodash'

export default {
    props: {
        titlePrefix: {
            type: String,
            default: '',
        },
        model: {
            type: Object,
            required: true,
            validator: model => {
                return model instanceof Activation
            },
        },
    },

    components: {
        FormActions,
        ModelSelect,
    },

    mixins: [validationMixin],

    data: () => {
        return {
            loading: false,
            loadingModules: true,
            modules: [],
            planPackageModel: new PlanPackage(),
        }
    },

    validations() {
        return {
            model: {
                value: { required, between: between(0.01, 999999.99) },
                installments: { required, between: between(1, 10) },
                amount: {
                    requiredIf: requiredIf(() => {
                        return this.model.module_id || this.model.activatable_id
                    }),
                    between: between(1, 10),
                },
            },
        }
    },

    computed: {
        title() {
            return this.$t('forms.title', {
                prefix: this.titlePrefix,
                resource: this.$tc(
                    'resources.activation.name',
                    1,
                ).toLowerCase(),
            })
        },

        activation() {
            return this.model
        },

        typeModule() {
            return Activation.typeModule
        },

        typePlanPackage() {
            return Activation.typePlanPackage
        },

        category_id() {
            return this.$route.params.category_id
        },

        valueErrors() {
            const errors = []
            if (!this.$v.model.value.$dirty) return errors

            const label = this.$t(
                'resources.activation.attributes.value',
            ).toLowerCase()

            !this.$v.model.value.required &&
                errors.push(this.$t('forms.errors.required', { label: label }))
            !this.$v.model.value.between &&
                errors.push(this.$t('forms.errors.between'), {
                    label: label,
                    min: '0.01',
                    max: '999999.99',
                })

            return errors
        },

        installmentsErrors() {
            const errors = []
            if (!this.$v.model.installments.$dirty) return errors

            const label = this.$t(
                'resources.activation.attributes.installments',
            ).toLowerCase()

            !this.$v.model.installments.required &&
                errors.push(this.$t('forms.errors.required', { label: label }))
            !this.$v.model.installments.between &&
                errors.push(
                    this.$t('forms.errors.between', {
                        label: label,
                        min: 1,
                        max: 10,
                    }),
                )

            return errors
        },

        amountErrors() {
            const errors = []
            if (!this.$v.model.amount.$dirty) return errors

            const label = this.$t(
                'resources.activation.attributes.amount',
            ).toLowerCase()

            !this.$v.model.amount.requiredIf &&
                errors.push(this.$t('forms.errors.required', { label: label }))

            !this.$v.model.amount.between &&
                errors.push(
                    this.$t('forms.errors.between', {
                        label: label,
                        min: 1,
                        max: 10,
                    }),
                )

            return errors
        },
    },

    methods: {
        async loadModules() {
            let res = await new Module().paginate(999, 1)

            res.data.map(item => {
                this.modules.push({
                    id: parseInt(item.id),
                    name: item.name,
                })
            })
        },

        async save() {
            this.loading = true

            this.$v.$touch()

            if (this.$v.$invalid) {
                this.loading = false
                return
            }

            try {
                this.activation.activatable_id = isObject(
                    this.activation.activatable_id,
                )
                    ? this.activation.activatable_id.id
                    : this.activation.activatable_id

                this.activation.category_id = this.category_id
                this.activation.module_id =
                    Activation.typeModule === this.activation.activatable_type
                        ? this.activation.activatable_id
                        : null
                await this.activation.save()
            } finally {
                this.loading = false
            }

            this.$bus.$emit(
                'notify',
                'success',
                this.$t('resources.feedbacks.success.save', {
                    resource: this.$tc('resources.activation.name', 1),
                }),
            )

            this.$emit('saved')
        },
    },

    created() {
        this.loadModules().then(() => {
            this.loadingModules = false
        })
    },
}
</script>
